import * as React from 'react';
import { Table, SortableTableHeader } from 'soprasteria-ui-library';
import { IAccessToPatientTreatmentRelatedEntity, IPatientTreatment } from '../../../models/IPatientTreatment';
import { dateTimeToDateString } from '../../../helpers/DateHelper';
import { PATIENT, EMERGENCY_TREATMENT, REFERRAL, ORTHODONTIC_REFERRAL, RISK_POT_REPORT, TREATMENT_REPORT } from '../../../utils/RouteConstants';
import { Link, useHistory } from 'react-router-dom';
import { formatCurrency } from '../../../helpers/CurrencyHelper';
import { formatPersonalSecurityNumberAndOrganisationNumber } from '../../../helpers/PersonalSecurityNumberAndOrganisationNumberHelper';
import { useAuth } from '../../../App';
import { PatientTreatmentSearchApi } from '../../../api/PatientTreatmentSearchApi';
import { NoAccessModal } from './NoAccessModal';

interface IPatientTreatmentSearchTable {
    patientTreatments: IPatientTreatment[];
}

export const PatientTreatmentSearchTable: React.FunctionComponent<IPatientTreatmentSearchTable> = ({ patientTreatments }) => {
    const auth = useAuth();
    const patientTreatmentSearchApi = new PatientTreatmentSearchApi(auth);
    const [noAccessModalText, setNoAccessModalText] = React.useState<string>("");
    const history = useHistory();
    let noAccessModalId = "no-access-modal";

    const renderEntityLink = (patientTreatment: IPatientTreatment) => {
        const linkText = patientTreatment.treatmentReportId ? "Till vårdrapport" : patientTreatment.entityNumber;
        return <Link onClick={(e) => openEntity(e, patientTreatment)} to={`#`}>{linkText}</Link>
    }

    const openEntity = (e: React.MouseEvent, patientTreatment: IPatientTreatment) => {
        e.preventDefault();
        const entityTypeQuery = getEntityTypeQuery(patientTreatment);
        
        patientTreatmentSearchApi.canAccessRelatedEntity(patientTreatment.patientTreatmentId!, (data: IAccessToPatientTreatmentRelatedEntity) => {
            if (data.canAccess) {
                history.push(`/${PATIENT}/${patientTreatment.patientGuidId}/${entityTypeQuery}`, history.location.state);
            }
            if (!data.canAccess) {
                setNoAccessModalText(data.noAccessMessage!);
                document.getElementById(`open-${noAccessModalId}`)?.click();
            } 
        });
    }

    const getEntityTypeQuery = (patientTreatment: IPatientTreatment) => {
        if (patientTreatment.emergencyTreatmentId) {
            return `${EMERGENCY_TREATMENT}/${patientTreatment.emergencyTreatmentId}`;
        }
        else if (patientTreatment.referralId) {
            return `${REFERRAL}/${patientTreatment.referralId}`;
        }
        else if (patientTreatment.orthodonticReferralId) {
            return `${ORTHODONTIC_REFERRAL}/${patientTreatment.orthodonticReferralId}`;
        }
        else if (patientTreatment.riskPotReportId) {
            return `${RISK_POT_REPORT}/${patientTreatment.riskPotReportId}`;
        }
        else if (patientTreatment.treatmentReportId) {
            return `${TREATMENT_REPORT}/${patientTreatment.treatmentReportId}`;
        }
        else {
            return null;
        }
    }

    const showEntityColumn = () => {
        return patientTreatments.some(data => data.treatmentReportId !== undefined || data.entityNumber !== "");
    }

    return (
        <section>
            <Table>
                <thead>
                    <tr>
                        {
                            showEntityColumn() ?
                                <th>Ärendenummer</th>
                                : null
                        }
                        <SortableTableHeader propertyToSortBy="Patient.PersonalSecurityId" headerText="Personnummer" />
                        <th>Organisationsnummer</th>
                        <th>Mottagningsnummer</th>
                        <th>Mottagningsnamn</th>
                        <SortableTableHeader propertyToSortBy="TreatmentDate" headerText="Behandlingsdatum" />
                        <SortableTableHeader propertyToSortBy="TreatmentCodeNavigation.Code" headerText="Åtgärdskod" />
                        <th>Summa</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        patientTreatments.length > 0 ?
                            patientTreatments.map((treatment, key) =>
                                <tr key={key}>
                                    {
                                        showEntityColumn() ?
                                            <td>{renderEntityLink(treatment)}</td>
                                            : null
                                    }
                                    <td className="table-data-link">{formatPersonalSecurityNumberAndOrganisationNumber(treatment.patientSocialSecurityNumber)}</td>
                                    <td className="table-data-link">{treatment.careProvider?.orgNum}</td>
                                    <td className="table-data-link">{treatment.careProvider?.clinicNumber}</td>
                                    <td className="table-data-link">{treatment.careProvider?.name}</td>
                                    <td className="table-data-link">{dateTimeToDateString(treatment.treatmentDate)}</td>
                                    <td className="table-data-link">{treatment.code}</td>
                                    <td className="text-right text-nowrap table-data-link">{formatCurrency(treatment.amount ?? 0)}</td>
                                    <td className="table-data-link">{treatment.patientTreatmentStatus}</td>
                                </tr>
                            )
                            : <tr><td colSpan={showEntityColumn() ? 9 : 8}>Det finns inga åtgärdskoder som matchar sökningen</td></tr>
                    }
                </tbody>
            </Table>
            <NoAccessModal noAccessText={noAccessModalText} id={noAccessModalId} />            
        </section>
    )
}
